"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeckSlideSchema = exports.DeckV1Schema = exports.deckV1Schema = exports.deckSlideSchema = exports.deckDataGapSchema = exports.deckSourceSchema = exports.deckStatSchema = exports.deckClaimSchema = exports.deckEvidenceSchema = void 0;
const zod_1 = require("zod");
/** The only evidence states a deck author may attach to a claim or figure. */
exports.deckEvidenceSchema = zod_1.z.discriminatedUnion('confidence', [
    zod_1.z.object({
        confidence: zod_1.z.literal('measured'),
        sourceIds: zod_1.z.array(zod_1.z.string().min(1)).min(1),
    }),
    zod_1.z.object({
        confidence: zod_1.z.literal('reported'),
        sourceIds: zod_1.z.array(zod_1.z.string().min(1)).min(1),
    }),
    zod_1.z.object({
        confidence: zod_1.z.literal('inference'),
        sourceIds: zod_1.z.array(zod_1.z.string().min(1)).min(1),
    }),
    zod_1.z.object({
        confidence: zod_1.z.literal('estimate'),
        method: zod_1.z.string().min(1).max(500),
        sourceIds: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    }),
    zod_1.z.object({
        confidence: zod_1.z.literal('unavailable'),
        note: zod_1.z.string().min(1).max(500),
    }),
]);
exports.deckClaimSchema = zod_1.z.object({
    text: zod_1.z.string().min(1).max(600),
    evidence: exports.deckEvidenceSchema,
});
exports.deckStatSchema = zod_1.z.object({
    label: zod_1.z.string().min(1).max(100),
    value: zod_1.z.union([zod_1.z.number().finite(), zod_1.z.string().min(1).max(100)]),
    suffix: zod_1.z.string().max(30).optional(),
    detail: zod_1.z.string().max(280).optional(),
    evidence: exports.deckEvidenceSchema,
});
exports.deckSourceSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(100),
    title: zod_1.z.string().min(1).max(200),
    kind: zod_1.z.enum(['website', 'ad-library', 'review-site', 'search', 'keyword-planner', 'operator', 'other']),
    url: zod_1.z.string().max(2048).optional(),
    accessedAt: zod_1.z
        .string()
        .min(10)
        .max(100)
        .regex(/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/, 'Expected an ISO-compatible date or timestamp.'),
});
exports.deckDataGapSchema = zod_1.z.object({
    what: zod_1.z.string().min(1).max(300),
    why: zod_1.z.string().min(1).max(500),
    how: zod_1.z.string().min(1).max(500),
});
const titleSchema = zod_1.z.string().min(1).max(160);
const subtitleSchema = zod_1.z.string().min(1).max(320);
const bodySchema = zod_1.z.string().min(1).max(600);
const labelSchema = zod_1.z.string().min(1).max(100);
const sectionSchema = zod_1.z.string().min(1).max(100).optional();
const evidenceContextFields = {
    evidence: exports.deckEvidenceSchema.optional(),
};
const agendaItemSchema = zod_1.z.object({
    number: zod_1.z.string().min(1).max(10),
    title: titleSchema,
    description: zod_1.z.string().max(280).optional(),
});
const listItemSchema = zod_1.z.object({
    ...evidenceContextFields,
    title: titleSchema,
    body: bodySchema.optional(),
    claim: exports.deckClaimSchema.optional(),
});
const stageSchema = zod_1.z.object({
    ...evidenceContextFields,
    label: labelSchema,
    title: titleSchema,
    body: bodySchema.optional(),
    claim: exports.deckClaimSchema.optional(),
});
const personaSchema = zod_1.z.object({
    ...evidenceContextFields,
    name: titleSchema,
    role: labelSchema,
    need: bodySchema,
    claim: exports.deckClaimSchema.optional(),
});
const cardSchema = zod_1.z.object({
    ...evidenceContextFields,
    value: labelSchema.optional(),
    title: titleSchema,
    body: bodySchema.optional(),
    claim: exports.deckClaimSchema.optional(),
});
const columnSchema = zod_1.z.object({
    ...evidenceContextFields,
    title: titleSchema,
    items: zod_1.z.array(bodySchema).min(1).max(5),
    claim: exports.deckClaimSchema.optional(),
});
const matrixCellSchema = zod_1.z.object({
    ...evidenceContextFields,
    x: labelSchema,
    y: labelSchema,
    label: titleSchema,
    body: zod_1.z.string().max(280).optional(),
    claim: exports.deckClaimSchema.optional(),
});
const barSchema = zod_1.z.object({
    label: labelSchema,
    value: zod_1.z.number().finite(),
    evidence: exports.deckEvidenceSchema,
});
const positioningPointSchema = zod_1.z.object({
    label: labelSchema,
    x: zod_1.z.number().finite(),
    y: zod_1.z.number().finite(),
    evidence: exports.deckEvidenceSchema,
});
const quoteSchema = zod_1.z.object({
    quote: zod_1.z.string().min(1).max(500),
    attribution: zod_1.z.string().min(1).max(200),
    category: labelSchema.optional(),
    evidence: exports.deckEvidenceSchema,
});
const teamMemberSchema = zod_1.z.object({
    ...evidenceContextFields,
    name: titleSchema,
    role: labelSchema,
    bio: zod_1.z.string().max(280).optional(),
});
const timelineStepSchema = zod_1.z.object({
    ...evidenceContextFields,
    label: labelSchema,
    title: titleSchema,
    body: bodySchema,
    owner: labelSchema.optional(),
});
const mechanismSchema = zod_1.z.object({
    ...evidenceContextFields,
    label: labelSchema,
    title: titleSchema,
    body: bodySchema,
    conclusion: bodySchema,
});
/**
 * Content contracts for all renderer layouts. Geometry intentionally does not
 * belong here: the renderer owns the 1920 × 1080 composition.
 */
exports.deckSlideSchema = zod_1.z.discriminatedUnion('layout', [
    zod_1.z.object({
        layout: zod_1.z.literal('cover'),
        ...evidenceContextFields,
        eyebrow: labelSchema,
        title: titleSchema,
        subtitle: subtitleSchema.optional(),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('agenda'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        items: zod_1.z.array(agendaItemSchema).length(3),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('stat-trio-callout'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        callout: exports.deckClaimSchema,
        stats: zod_1.z.array(exports.deckStatSchema).length(4),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('numbered-list'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        items: zod_1.z.array(listItemSchema).min(2).max(5),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('stat-grid'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        stats: zod_1.z.array(exports.deckStatSchema).length(6),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('spectrum-stages'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        topAxisLabel: labelSchema.optional(),
        bottomAxisLabel: labelSchema.optional(),
        marker: labelSchema.optional(),
        stages: zod_1.z.array(stageSchema).length(5),
        conclusion: bodySchema.optional(),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('persona-columns'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        personas: zod_1.z.array(personaSchema).length(4),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('two-big-cards-warning'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        cards: zod_1.z.array(cardSchema).length(2),
        warning: exports.deckClaimSchema,
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('owns-columns'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        columns: zod_1.z.array(columnSchema).length(5),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('big-number-stats'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        stats: zod_1.z.array(exports.deckStatSchema).length(5),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('matrix'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        xAxis: labelSchema,
        yAxis: labelSchema,
        rowLabels: zod_1.z.array(labelSchema).length(6).optional(),
        columnLabels: zod_1.z.array(labelSchema).length(5).optional(),
        cells: zod_1.z.array(matrixCellSchema).length(4),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('bar-chart-cards'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        metricLabel: labelSchema.optional(),
        bars: zod_1.z.array(barSchema).length(5),
        cards: zod_1.z.array(cardSchema).length(3),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('positioning-map'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        xAxis: labelSchema,
        yAxis: labelSchema,
        currentLabel: labelSchema.optional(),
        mechanism: mechanismSchema.optional(),
        points: zod_1.z.array(positioningPointSchema).min(2).max(8),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('big-idea'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        idea: exports.deckClaimSchema,
        pillars: zod_1.z.array(cardSchema).length(3),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('quote-trio'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        quotes: zod_1.z.array(quoteSchema).length(3),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('numbered-columns'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        columns: zod_1.z.array(columnSchema).length(4),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('team-roster'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        members: zod_1.z.array(teamMemberSchema).min(2).max(6),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('timeline'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        steps: zod_1.z.array(timelineStepSchema).min(2).max(6),
        conclusion: bodySchema.optional(),
    }),
    zod_1.z.object({
        layout: zod_1.z.literal('data-needed'),
        ...evidenceContextFields,
        section: sectionSchema,
        title: titleSchema,
        gaps: zod_1.z.array(exports.deckDataGapSchema).min(1).max(8),
    }),
]);
exports.deckV1Schema = zod_1.z.object({
    version: zod_1.z.literal(1),
    meta: zod_1.z.object({
        brand: zod_1.z.string().min(1).max(200),
        date: zod_1.z.string().min(1).max(100),
        dataGaps: zod_1.z.array(exports.deckDataGapSchema).max(8).default([]),
    }),
    sources: zod_1.z.array(exports.deckSourceSchema).max(100),
    slides: zod_1.z.array(exports.deckSlideSchema).max(24),
});
// PascalCase aliases make the schema convenient to consume at tool boundaries.
exports.DeckV1Schema = exports.deckV1Schema;
exports.DeckSlideSchema = exports.deckSlideSchema;
